import React, { useState } from 'react';
import {
  HStack,
  IconButton,
  Tooltip,
  useToast,
  useColorModeValue,
} from '@chakra-ui/react';
import { CopyIcon, CheckIcon, RepeatIcon } from '@chakra-ui/icons';
import { FiThumbsUp, FiThumbsDown } from 'react-icons/fi';
import './MessageActions.css';

interface MessageActionsProps {
  content: string;
  messageIndex: number;
  isUser: boolean;
  onRegenerate?: (index: number) => void;
  onFeedback?: (index: number, feedback: 'up' | 'down') => void;
}

const MessageActions: React.FC<MessageActionsProps> = ({
  content,
  messageIndex,
  isUser,
  onRegenerate,
  onFeedback,
}) => {
  const [copied, setCopied] = useState(false);
  const [feedback, setFeedback] = useState<'up' | 'down' | null>(null);
  const toast = useToast();

  const iconColor = useColorModeValue('gray.600', 'gray.400');
  const activeColor = useColorModeValue('primary.500', 'primary.300');
  const bgHover = useColorModeValue('gray.100', 'gray.700');

  const handleCopy = async () => {
    try {
      // Remove markdown formatting for cleaner copy
      const plainText = content
        .replace(/\*\*(.*?)\*\*/g, '$1') // Remove bold
        .replace(/\*(.*?)\*/g, '$1') // Remove italic
        .replace(/`(.*?)`/g, '$1') // Remove inline code
        .replace(/\[Button:.*?\]/g, '') // Remove button syntax
        .trim();

      await navigator.clipboard.writeText(plainText);
      setCopied(true);

      toast({
        title: 'Copied to clipboard',
        status: 'success',
        duration: 2000,
        isClosable: true,
        position: 'top',
      });

      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast({
        title: 'Failed to copy',
        status: 'error',
        duration: 2000,
        isClosable: true,
        position: 'top',
      });
    }
  };

  const handleFeedback = (type: 'up' | 'down') => {
    const newFeedback = feedback === type ? null : type;
    setFeedback(newFeedback);

    if (onFeedback && newFeedback) {
      onFeedback(messageIndex, newFeedback);
    }

    toast({
      title: newFeedback
        ? `Thanks for your ${type === 'up' ? 'positive' : ''} feedback!`
        : 'Feedback removed',
      status: 'info',
      duration: 2000,
      isClosable: true,
      position: 'top',
    });
  };

  const handleRegenerate = () => {
    if (onRegenerate) {
      onRegenerate(messageIndex);
      toast({
        title: 'Regenerating response...',
        status: 'info',
        duration: 2000,
        isClosable: true,
        position: 'top',
      });
    }
  };

  return (
    <HStack
      spacing={1}
      className={`message-actions ${feedback ? 'has-feedback' : ''}`}
      opacity={0}
      transition='opacity 0.2s'
      mt={1}
    >
      {/* Copy button */}
      <Tooltip label={copied ? 'Copied!' : 'Copy message'} placement='top'>
        <IconButton
          aria-label='Copy message'
          icon={copied ? <CheckIcon /> : <CopyIcon />}
          size='xs'
          variant='ghost'
          color={copied ? activeColor : iconColor}
          _hover={{ bg: bgHover }}
          onClick={handleCopy}
        />
      </Tooltip>

      {/* Feedback buttons - only for AI messages */}
      {!isUser && (
        <>
          <Tooltip label='Good response' placement='top'>
            <IconButton
              aria-label='Thumbs up'
              icon={<FiThumbsUp />}
              size='xs'
              variant='ghost'
              color={feedback === 'up' ? activeColor : iconColor}
              _hover={{ bg: bgHover }}
              onClick={() => handleFeedback('up')}
            />
          </Tooltip>

          <Tooltip label='Bad response' placement='top'>
            <IconButton
              aria-label='Thumbs down'
              icon={<FiThumbsDown />}
              size='xs'
              variant='ghost'
              color={feedback === 'down' ? 'red.500' : iconColor}
              _hover={{ bg: bgHover }}
              onClick={() => handleFeedback('down')}
            />
          </Tooltip>

          {/* Regenerate button - only for AI messages and not the first message */}
          {messageIndex > 0 && (
            <Tooltip label='Regenerate response' placement='top'>
              <IconButton
                aria-label='Regenerate response'
                icon={<RepeatIcon />}
                size='xs'
                variant='ghost'
                color={iconColor}
                _hover={{ bg: bgHover }}
                onClick={handleRegenerate}
              />
            </Tooltip>
          )}
        </>
      )}
    </HStack>
  );
};

export default MessageActions;
